import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Badge } from "@/components/ui/badge.tsx"
import { AccountingWizardPanel } from "./AccountingWizardPanel"
import { SplitWizPanel } from "./SplitWizPanel"
import type { AccountingWizardOutcome } from "@/api/accounting_wizard"
import type { SplitWizOutcome } from "@/api/split_wizard"
import {
  BulkCodingWizResultsOpen,
  BulkCodingWizState,
  bulk_coding_wiz_counts,
  bulk_coding_wiz_row_failed,
  bulk_coding_wiz_summary,
  type BulkCodingWizRowState,
  type BulkCodingWizRun,
} from "./bulk_coding_wiz_state"
import { Link } from "wouter-preact"

function deduction_detail_href(deduction_id: string, is_accounting: boolean): string {
  return is_accounting ? `/accounting/transaction/${deduction_id}` : `/deduction/${deduction_id}`
}

/** What to call a row in the list: its invoice number when the run captured one, else a short id. */
export function bulk_coding_wiz_row_label(run: BulkCodingWizRun, deduction_id: string): string {
  const label = run.labels[deduction_id]
  if (label) return label
  return deduction_id.length > 12 ? `${deduction_id.slice(0, 8)}…` : deduction_id
}

function RowStatusBadge({ row }: { row: BulkCodingWizRowState | undefined }) {
  if (!row || row.status === "queued") {
    return <Badge variant="outline">Queued</Badge>
  }
  if (row.status === "running") {
    return <Badge variant="sky">Running</Badge>
  }
  if (bulk_coding_wiz_row_failed(row)) {
    return <Badge variant="destructive">Failed</Badge>
  }
  return <Badge variant="success">Coded</Badge>
}

function BulkCodingWizResultRow({ run, deduction_id }: { run: BulkCodingWizRun; deduction_id: string }) {
  const row = run.rows[deduction_id]
  const running = row?.status === "running"
  const message = running ? row.message : null

  let body = null
  if (row?.status === "error") {
    body = <p class="text-sm text-red-600">{row.error}</p>
  } else if (run.mode === "ledger") {
    body = (
      <AccountingWizardPanel
        outcome={row?.status === "done" ? (row.outcome as AccountingWizardOutcome) : null}
        isLoading={running}
        loading_status_message={message}
      />
    )
  } else {
    body = (
      <SplitWizPanel
        outcome={row?.status === "done" ? (row.outcome as SplitWizOutcome) : null}
        isLoading={running}
        loading_status_message={message}
      />
    )
  }

  return (
    <div class="border rounded-lg p-3 bg-gray-50/80">
      <div class="flex items-center justify-between gap-2 mb-2">
        <Link
          href={deduction_detail_href(deduction_id, run.is_accounting)}
          class="text-sm font-medium text-primary underline underline-offset-2 truncate">
          {bulk_coding_wiz_row_label(run, deduction_id)}
        </Link>
        <RowStatusBadge row={row} />
      </div>
      {!row || row.status === "queued" ? <p class="text-sm text-gray-500">Waiting for a free slot…</p> : body}
    </div>
  )
}

/**
 * Results of the current bulk CodingWiz run, one card per transaction, updating live as each
 * one streams in. Opened from the progress panel or the completion toast; it reads the shared
 * run state so it can be closed and reopened without losing anything.
 */
export function BulkCodingWizSheet() {
  const run = BulkCodingWizState.use()
  const open = BulkCodingWizResultsOpen.use()
  const counts = bulk_coding_wiz_counts(run)

  return (
    <Sheet open={open && run !== null} onOpenChange={(value: boolean) => BulkCodingWizResultsOpen.set(value)}>
      <SheetContent class="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>CodingWiz · {bulk_coding_wiz_summary(counts)}</SheetTitle>
          <SheetDescription>
            Bulk results using your saved CodingWiz preferences (
            {run?.mode === "split" ? "multi-line split" : "single ledger code"}).
          </SheetDescription>
        </SheetHeader>
        <div class="mt-4 space-y-4">
          {run
            ? run.deduction_ids.map((deduction_id: string) => (
                <BulkCodingWizResultRow key={deduction_id} run={run} deduction_id={deduction_id} />
              ))
            : null}
        </div>
      </SheetContent>
    </Sheet>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("bulk_coding_wiz_row_label", () => {
    const run: BulkCodingWizRun = {
      run_id: 1,
      mode: "ledger",
      is_accounting: true,
      deduction_ids: ["0192abcd-1234-5678-9abc-def012345678", "short"],
      labels: { "0192abcd-1234-5678-9abc-def012345678": "INV-100" },
      rows: {},
      started_at: "2026-09-10T00:00:00Z",
      finished_at: null,
      minimized: false,
    }

    it("prefers the invoice number captured at start", () => {
      expect(bulk_coding_wiz_row_label(run, "0192abcd-1234-5678-9abc-def012345678")).toBe("INV-100")
    })

    it("falls back to a shortened id", () => {
      expect(bulk_coding_wiz_row_label(run, "short")).toBe("short")
      expect(bulk_coding_wiz_row_label(run, "0192abcd-ffff-ffff-ffff-ffffffffffff")).toBe("0192abcd…")
    })
  })
}
